import React from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Compress, Expand, Refresh, AutoFixHigh } from "@mui/icons-material";
import { inputStyles, renderLabel, renderDescriptionTooltip } from "../common";
import { LoadingOperation } from "./types";

interface NarrativeEditorProps {
  id: string;
  value: string;
  onChange: (value: string | undefined) => void;
  disabled: boolean;
  readonly: boolean;
  loadingOperation: LoadingOperation;
  placeholder?: string;
  label: string;
  required: boolean;
  minRows: number;
  /**
   * Upper bound on auto-grown rows before the textarea scrolls internally.
   * The schema-bound full editor passes 12. When omitted the textarea grows
   * to fit its entire content (no internal scroll) and the parent container
   * does the scrolling — used by the narrative-only editor.
   */
  maxRows?: number;
  description?: string;
  /** Focus on mount — set when the field appears via the source-linked
   * view's "Edit text" swap so the caret lands where the click intended. */
  autoFocus?: boolean;
  /** Fires when the textarea gains focus — hosts latch their view/edit swap
   * on it so an externally-arriving cited value can't unmount the field the
   * officer is typing in. */
  onFocus?: () => void;
}

export const NarrativeEditor: React.FC<NarrativeEditorProps> = ({
  id,
  value,
  onChange,
  disabled,
  readonly,
  loadingOperation,
  placeholder,
  label,
  required,
  minRows,
  maxRows,
  description,
  autoFocus,
  onFocus,
}) => {
  // Helper to get operation-specific labels and icons
  const getOperationDetails = (operation: LoadingOperation) => {
    switch (operation) {
      case "condense":
        return {
          label: "Condensing narrative...",
          icon: <Compress color="primary" />,
        };
      case "expatiate":
        return {
          label: "Expanding narrative...",
          icon: <Expand color="primary" />,
        };
      case "addContext":
        return {
          label: "Regenerating with context...",
          icon: <Refresh color="primary" />,
        };
      default:
        return {
          label: "Processing...",
          icon: <AutoFixHigh color="primary" />,
        };
    }
  };

  return (
    <Box position="relative">
      <TextField
        id={id}
        fullWidth
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || undefined)}
        disabled={disabled || loadingOperation !== null}
        slotProps={{
          input: {
            readOnly: readonly,
            endAdornment: description ? (
              <InputAdornment position="end">
                {renderDescriptionTooltip(description)}
              </InputAdornment>
            ) : null,
          },
        }}
        placeholder={placeholder}
        label={renderLabel(label, !!required)}
        required={required}
        autoFocus={autoFocus}
        onFocus={onFocus}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        variant="outlined"
        sx={{ ...inputStyles }}
      />
      {loadingOperation !== null && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(4px)",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "primary.50",
                position: "relative",
              }}
            >
              <CircularProgress
                size={56}
                thickness={2}
                sx={{
                  position: "absolute",
                  color: "primary.main",
                }}
              />
              {getOperationDetails(loadingOperation).icon}
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {getOperationDetails(loadingOperation).label}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              This may take a few moments
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};
